import React, { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, MessageCircle, Ruler, ShieldCheck, Truck, BadgeCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { SafeImage } from "@/components/SafeImage";

import { COMPANY, PRODUCT_CATEGORIES } from "@/mock";
import { StatPill, TypicalSpecsNote } from "@/pages/_bits";
import { Container, makeWhatsAppUrl, setTitle } from "@/pages/_shared";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const category = useMemo(
    () => PRODUCT_CATEGORIES.find((c) => c.id === categoryId),
    [categoryId]
  );

  useEffect(() => {
    setTitle(category ? category.name : "Products");
  }, [category]);

  if (!category) {
    return (
      <Container className="py-14">
        <div className="rounded-3xl border border-border/60 bg-background/70 p-8">
          <div className="text-lg font-extrabold">Category not found</div>
          <p className="mt-2 text-sm text-muted-foreground">Please go back to Products.</p>
          <Button className="mt-4" onClick={() => navigate("/products")}>
            View Products
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div>
      <section className="py-14">
        <Container>
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Link className="hover:text-foreground" to="/products">Products</Link>
            <span>/</span>
            <span className="text-foreground font-semibold">{category.name}</span>
          </div>

          <div className="mt-6 grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7">
              <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">{category.name}</h1>
              <p className="mt-4 text-base text-muted-foreground max-w-2xl">{category.short}</p>

              <div className="mt-6">
                <TypicalSpecsNote />
              </div>

              <div className="mt-10 grid gap-4">
                {category.products.map((p) => (
                  <Card key={p.slug} className="overflow-hidden border-border/60 bg-background/70">
                    <div className="grid gap-0 sm:grid-cols-12">
                      <div className="sm:col-span-4">
                        <div className="relative h-44 sm:h-full">
                          <SafeImage
                            src={p.image || category.image}
                            alt={`${p.name} - ${COMPANY.name}`}
                            imgClassName="h-full w-full object-cover"
                            fallbackLabel={p.name}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                          <div className="absolute left-3 top-3 flex flex-wrap gap-2">
                            {p.imageNote ? (
                              <Badge className="bg-white/15 text-white border-white/20 backdrop-blur">
                                {p.imageNote}
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="sm:col-span-8">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg font-extrabold tracking-tight">{p.name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{p.desc}</p>
                        </CardHeader>
                        <CardContent className="pt-0 pb-4">
                          <div className="flex flex-wrap gap-2">
                            {p.features.slice(0, 4).map((f) => (
                              <Badge key={f} variant="secondary" className="bg-secondary/60">
                                {f}
                              </Badge>
                            ))}
                          </div>
                          <div className="mt-4 flex flex-col sm:flex-row gap-2">
                            <Button
                              className="bg-slate-900 text-slate-50 hover:bg-slate-800"
                              onClick={() => navigate(`/product/${category.id}/${p.slug}`)}
                            >
                              View details <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              className="border-emerald-200/60 bg-emerald-50 text-emerald-950 hover:bg-emerald-100"
                              onClick={() =>
                                window.open(
                                  makeWhatsAppUrl(COMPANY.whatsapp, `Hello, I need pricing for ${p.name}.`),
                                  "_blank"
                                )
                              }
                            >
                              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp for price
                            </Button>
                          </div>
                        </CardContent>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-3xl border border-border/60 bg-background/70">
                <SafeImage
                  src={category.image}
                  alt={category.name}
                  imgClassName="h-72 w-full object-cover"
                  fallbackLabel={category.name}
                />
              </div>
              <div className="mt-4 grid gap-3">
                <StatPill icon={Truck} title="Northeast delivery" desc="Dispatch planning for Assam & region" />
                <StatPill icon={Ruler} title="Custom sizes" desc="Mesh, aperture, coating & packaging" />
                <StatPill icon={ShieldCheck} title="Quality-first" desc="Inspection + test-ready supply" />
                <div className="rounded-2xl border border-border/60 bg-background/70 p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="rounded-xl bg-amber-100 p-2 border border-amber-200 text-amber-950">
                      <BadgeCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-sm font-extrabold tracking-tight">Bulk order available</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Share quantity + location and we’ll confirm pricing and lead time.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
